import React, { useState, useCallback, useMemo, useEffect } from 'react';

import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  Row,
  getFilteredRowModel,
} from '@tanstack/react-table';

// Styled Components
import {
  Container,
  SubContainer,
  SubContainerRight,
  ContainerTables,
  ContainerSearchBar,
  Table,
  Thead,
  Th,
  ThRightTable,
  Tr,
  TrBody,
  Td,
  TableRight,
  Tbody,
  TbodyRight,
  Img,
  Input,
  TdRightTable,
  StyledInput,
} from './styles';

// Components
import { Typography } from '../../../Typography';
import SearchBar from '../../../../inputs/SearchBar';

//Icons
import ArrowIcon from '../../../../../assets/icons/grey-right-arrow-icon.svg';
import RemoveIcon from '../../../../../assets/icons/remove-file-icon.svg';

// Components for clickable rows
import { DoubleTapRow } from '../../components/DoubleTapRow';

// Types
import {
  dataTable,
  tableProps,
  columnNames,
  getColumnId,
  onChangeInputTypes,
  variantThRight,
  variantThLeft,
} from './types';

// Mocks
import { mockData } from './mocks';
import { OrderState } from '../../../../../types/orders';

// Utils
import { normalizeDecimalInput } from '../../../../../utils/decimalFormat';
import { textSearchFilter } from '../../textSearchFilter';

export const MaterialSelectionTable = ({
  data,
  defaultValues = [],
  register,
  setValue,
  onAdd,
  onDelete,
  onUpdate,
  ...props
}: tableProps): JSX.Element => {
  const [selectedRows, setSelectedRows] = useState<Row<dataTable>[]>([]);
  const [dataTableRight, setDataTableRight] =
    useState<dataTable[]>(defaultValues);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const dataTable = data ? data : mockData;

  // Clear highlight after 1.5 seconds
  useEffect(() => {
    if (highlightedRowId) {
      const timer = setTimeout(() => setHighlightedRowId(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [highlightedRowId]);

  const columnHelper = createColumnHelper<dataTable>();
  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      const newData: dataTable = {
        codification: row.original.codification,
        name: row.original.name,
        typeOfMeasurement: row.original.typeOfMeasurement,
        id: row.original.id,
        materialId: row.original.materialId,
      };

      const id = row.original.id;

      const setValues = () => {
        setValue(
          `materialsTable[${id}].codification`,
          row.original.codification
        );
        setValue(`materialsTable[${id}].name`, row.original.name);
        setValue(
          `materialsTable[${id}].typeOfMeasurement`,
          row.original.typeOfMeasurement
        );

        setValue(`materialsTable[${id}].ut`, '');
        setValue(`materialsTable[${id}].id`, row.original.id);
        setValue(`materialsTable[${id}].materialId`, row.original.materialId);
      };

      const duplicate =
        selectedRows.some(
          (element) => element.original.id === row.original.id
        ) || dataTableRight.some((element) => element.id === row.original.id);
      !duplicate && setValues();
      !duplicate && setDataTableRight([...dataTableRight, newData]);
      !duplicate && setSelectedRows([...selectedRows, row]);
      onAdd && !duplicate && onAdd(row.original.id as string);
    },
    [dataTableRight, selectedRows]
  );

  const removeRow = useCallback(
    (row: Row<dataTable>) => {
      setDataTableRight((element) =>
        element.filter((item) => item.id !== row.original.id)
      );

      setSelectedRows((element) =>
        element.filter((item) => item.original.id !== row.original.id)
      );

      setValue(`materialsTable[${row.original.id}]`, undefined);
      onDelete &&
        onDelete(
          row.original.id as string,
          row.original.materialUnitId as string
        );
    },
    [dataTableRight]
  );

  const columnsLeft = useMemo(
    () => [
      columnHelper.accessor('add' as any, {
        header: () => (
          <Typography colorText="white" size="S">
            +
          </Typography>
        ),
        cell: () => <Img src={ArrowIcon} />,
      }),
      columnHelper.accessor(getColumnId(columnNames.materialId.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.materialId.label}
          </Typography>
        ),
        cell: (info) => (
          <Typography size="S">{info.getValue() as string}</Typography>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.name.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.name.label}
          </Typography>
        ),
        cell: (info) => (
          <StyledInput value={info.getValue() as string} readOnly />
        ),
        // Sin acentos y por palabras (antes 'includesString', solo esta
        // columna): el buscador mira también las columnas de texto que se ven.
        filterFn: textSearchFilter((row) => [row.materialId, row.name, row.codification, row.subCodification]),
      }),
      columnHelper.accessor(getColumnId(columnNames.codification.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.codification.label}
          </Typography>
        ),
        cell: (info) => (
          <Typography size="S">{info.getValue() as string}</Typography>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.subCodification.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.subCodification.label}
          </Typography>
        ),
        cell: (info) => (
          <Typography size="S">{info.getValue() as string}</Typography>
        ),
      }),
    ],
    [selectedRows]
  );

  const columnsRight = useMemo(
    () => [
      columnHelper.accessor(getColumnId(columnNames.materialId.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.materialId.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`materialsTable[${info.row.original.id}].materialId`)}
            disabled={true}
            type="string"
          ></Input>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.name.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.name.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`materialsTable[${info.row.original.id}].name`)}
            disabled={true}
            type="string"
          ></Input>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.ut.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.ut.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`materialsTable[${info.row.original.id}].ut`)}
            disabled={
              data?.parentExpeditionOrderStatus === OrderState.Annulled ||
              data?.parentExpeditionOrderStatus === OrderState.End ||
              data?.state === OrderState.Annulled
            }
            type="text"
            inputMode="decimal"
            pattern="^\d*([.,]\d{0,2})?$"
            onChange={(event) => {
              const normalized = normalizeDecimalInput(event.target.value);
              setValue(
                `materialsTable[${info.row.original.id}].ut`,
                normalized
              );
              onUpdate &&
                onUpdate(
                  normalized,
                  info.row.original.id as string,
                  info.row.original.materialUnitId as string,
                  `materialsTable[${info.row.original.id}].ut` as string
                );
            }}
            onKeyDown={(e) => {
              // Bloquea letras y operadores; coma y punto se gestionan en onChange.
              if (e.key.length === 1 && !/[\d.,]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.typeOfMeasurement.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.typeOfMeasurement.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(
              `materialsTable[${info.row.original.id}].typeOfMeasurement`
            )}
            disabled={true}
            type="string"
          ></Input>
        ),
      }),
      columnHelper.accessor('remove' as any, {
        header: () => (
          <Typography colorText="white" size="S">
            -
          </Typography>
        ),
        cell: ({ row }) => (
          <Img
            src={RemoveIcon}
            onClick={() => {
              return (
                data?.parentExpeditionOrderStatus !== OrderState.Annulled &&
                data?.parentExpeditionOrderStatus !== OrderState.End &&
                data?.state !== OrderState.Annulled &&
                removeRow(row)
              );
            }}
          />
        ),
      }),
    ],
    [dataTableRight]
  );

  const table = useReactTable({
    data: dataTable,
    columns: columnsLeft,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const tableRight = useReactTable({
    data: dataTableRight,
    columns: columnsRight,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const allLeftRows = table.getCoreRowModel().rows;
    const similarTables = allLeftRows.filter((row) => {
      const code = row.original.codification;
      const matchingItem = dataTableRight.find((item) => item.id === code);
      if (matchingItem?.materialUnitId) {
        row.original.materialUnitId = matchingItem.materialUnitId;
      }
      return !!matchingItem;
    });

    setSelectedRows(similarTables);
  }, []);

  const onChangeInput = useCallback<onChangeInputTypes>((value) => {
    const material = table.getColumn(columnNames.name.id);
    material?.setFilterValue(value);
  }, []);

  return (
    <Container {...props}>
      <ContainerSearchBar>
        <SearchBar
          variant="form"
          onChangeInput={(e) => onChangeInput(e.target.value)}
        />
      </ContainerSearchBar>
      <ContainerTables>
        <SubContainer>
          <Table>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th variant={header.id as variantThLeft} key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isDisabled =
                  data?.parentExpeditionOrderStatus === OrderState.Annulled ||
                  data?.parentExpeditionOrderStatus === OrderState.End ||
                  data?.state === OrderState.Annulled;

                return (
                  <DoubleTapRow
                    key={row.id}
                    onSingleTap={() => !isDisabled && setHighlightedRowId(row.id)}
                    onDoubleTap={() => !isDisabled && handleClickRow(row)}
                    isDisabled={isDisabled}
                    singleClickOnDesktop={true}
                    StyledComponent={TrBody}
                    componentProps={{ $highlighted: highlightedRowId === row.id }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        variant={cell.id.slice(2) as variantThLeft}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </DoubleTapRow>
                );
              })}
            </Tbody>
          </Table>
        </SubContainer>
        <SubContainerRight>
          <TableRight>
            <Thead>
              {tableRight.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <ThRightTable
                      variant={header.id as variantThRight}
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </ThRightTable>
                  ))}
                </Tr>
              ))}
            </Thead>
            <TbodyRight>
              {tableRight.getRowModel().rows.map((row) => (
                <TrBody key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TdRightTable
                      variant={cell.id.slice(2) as variantThRight}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TdRightTable>
                  ))}
                </TrBody>
              ))}
            </TbodyRight>
          </TableRight>
        </SubContainerRight>
      </ContainerTables>
    </Container>
  );
};
